import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import type { GeneralBalanceData } from '@/entities/account';
import SettingsIcon from '@/shared/assets/settings-icon.svg?react';
import LogOutIcon from '@/shared/assets/logout-16-icon.svg?react';
import type { BalanceRow, LimitRow } from '../model';
import { AccountIdRow } from './AccountIdRow';
import { BalanceSection } from './BalanceSection';
import { LimitsSection } from './LimitsSection';
import { MenuRow } from './MenuRow';
import { LanguageRow } from './LanguageRow';
import { CurrencyRow } from './CurrencyRow';

type Props = {
  general: GeneralBalanceData;
  fullName: string;
  accountId: string;
  isVerified: boolean;
  isBalancesHidden: boolean;
  isTabletUp: boolean;
  balanceRows: BalanceRow[];
  limits: {
    rows: LimitRow[];
    periodEndLabel: string;
    basePrecission?: number;
    baseCode?: string;
    isVisible: boolean;
  };
  onOpenSettings: () => void;
  onOpenLimits: () => void;
  onLogout: () => void;
};

const Divider = () => <div className="h-px -mx-[0.4rem] bg-secondary-bg" />;

export function UserBalancePanel({
  general,
  fullName,
  accountId,
  isVerified,
  isBalancesHidden,
  isTabletUp,
  balanceRows,
  limits,
  onOpenSettings,
  onOpenLimits,
  onLogout,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <div className="flex flex-col gap-[0.8rem] px-[1.2rem] pt-[1.2rem]">
        {!isTabletUp && (
          <p className="headline4 text-primary truncate">{fullName}</p>
        )}

        {isVerified && accountId && <AccountIdRow accountId={accountId} />}

        <Button
          size="small"
          variant="outline"
          disabled
          className="w-full h-[2.8rem] rounded-[1rem] border-stroke shadow-none px-[1.2rem] py-0 gap-[0.4rem]">
          <img
            src="/assets/prime-badge.svg"
            alt=""
            className="size-[1.6rem] shrink-0"
          />
          {t('personalAccount.sidebarNavigation.prime.upgrade')}
        </Button>
      </div>

      <Divider />

      <BalanceSection
        rows={balanceRows}
        basePrecission={general.basePrecission}
        baseCode={general.baseCode}
        isBalancesHidden={isBalancesHidden}
        isTabletUp={isTabletUp}
      />

      {limits.isVisible && (
        <>
          <Divider />
          <LimitsSection
            rows={limits.rows}
            periodEndLabel={limits.periodEndLabel}
            basePrecission={limits.basePrecission}
            baseCode={limits.baseCode}
            onOpenFull={onOpenLimits}
          />
        </>
      )}

      <Divider />

      <div className="flex flex-col gap-[0.1rem] pb-[0.2rem]">
        <MenuRow
          icon={<SettingsIcon />}
          label={t('personalAccount.balanceDropdown.allSettings')}
          onClick={onOpenSettings}
        />
        <LanguageRow isTabletUp={isTabletUp} />
        <CurrencyRow isTabletUp={isTabletUp} />

        <div className="h-px -mx-[0.4rem] bg-secondary-bg my-[0.45rem]" />

        <MenuRow
          icon={<LogOutIcon />}
          label={t('common.logout')}
          destructive
          onClick={onLogout}
        />
      </div>
    </div>
  );
}
